import {
	AbstractControl,
	UntypedFormArray,
	UntypedFormControl,
	UntypedFormGroup,
	ValidationErrors,
	ValidatorFn,
} from '@angular/forms';
import {
	camelCaseToReadable,
	Company,
	isNilOrWhitespace,
} from 'company-finder-common';

export function requireIfBlueKnightCompany(company: Company): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (company.isBlueKnight) {
			return isEmptyInputValue(control.value) ? { required: true } : null;
		} else {
			return null;
		}
	};
}

export class RequireMoreThanJustSpaces {
	static required(control: AbstractControl): ValidationErrors | null {
		return isEmptyInputValue(control.value) ? { required: true } : null;
	}
}

export class MoneyRequiresCurrency {
	static isNullOrEmpty(str: string): boolean {
		return !str || str.length === 0;
	}

	// If the valueField has a value, a currency is required.
	static required(
		fields: {
			valueFields: string[];
			currencyField: string;
		},
		disabled: boolean = false
	): ValidatorFn {
		return (fg: UntypedFormGroup) => {
			if (disabled) {
				return null;
			}

			const currency = fg.get(fields.currencyField).value;

			let noneHaveValue = true;

			fields.valueFields.forEach((valueField) => {
				const raised = fg.get(valueField).value;
				noneHaveValue = noneHaveValue && this.isNullOrEmpty(raised);
			});

			return noneHaveValue || !this.isNullOrEmpty(currency)
				? null
				: { currencyRequired: true };
		};
	}
}

export function isEmptyInputValue(value: [] | string): boolean {
	// This is borrowed from Angular, but tweaked for our specific string-based use case, trimming the value.
	// https://github.com/angular/angular/blob/master/packages/forms/src/validators.ts
	// NOTE: The `value == null` below instead of === appears to have been intentional, to match null and undefined.
	return (
		value == null ||
		(Array.isArray(value) && value.length === 0) ||
		(typeof value === 'string' && value.trim().length === 0)
	);
}

/**
 * Recursively collects a simple string representation of errors
 * for an UntypedFormGroup (and any nested groups/arrays).
 */
// Mostly generated by genai.jnj.com with prompt:
// "What if I just wanted to start with a simple recursion through the UntypedFormGroup and collect the set of errors that exist at the level of the formgroup and its individual controls? It seems they could be simple "{required: true}" objects on individual controls, or an arbitrary object on the formgroup that a custom validator might emit. But, for now, I just want to build up a simple string, and I can worry about a more friendly representation of it later.""
export function collectUntypedFormErrors(
	control: AbstractControl,
	path: string = ''
): string[] {
	const messages: string[] = [];

	const qualifiedMsg = (label: string, msg: string) => {
		return `${!isNilOrWhitespace(label) ? label + ' ' : ''}${msg}`;
	};

	if (control instanceof UntypedFormGroup) {
		// Group-level errors (e.g., from a custom validator on the group)
		if (control.errors) {
			const label = path || '';
			messages.push(
				qualifiedMsg(label, formatErrorObject(control.errors))
			);
		}

		// Recurse into child controls
		Object.keys(control.controls).forEach((key) => {
			const child = control.controls[key];
			const childPath = path ? `${path}.${key}` : key;
			messages.push(...collectUntypedFormErrors(child, childPath));
		});
	} else if (control instanceof UntypedFormArray) {
		// Optional: treat arrays similarly
		if (control.errors) {
			const label = path || '';
			messages.push(
				qualifiedMsg(label, formatErrorObject(control.errors))
			);
		}
		control.controls.forEach((child, idx) => {
			const childPath = path ? `${path}[${idx}]` : `[${idx}]`;
			messages.push(...collectUntypedFormErrors(child, childPath));
		});
	} else if (control instanceof UntypedFormControl) {
		if (control.errors) {
			const label = path || '';
			messages.push(
				qualifiedMsg(label, formatErrorObject(control.errors))
			);
		}
	}

	return messages.map((msg) => camelCaseToReadable(msg));
}

/**
 * Helper to format an errors object into a simple string like "{ required, minLength=3 }"
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatErrorObject(errs: any): string {
	const keys = Object.keys(errs || {});
	if (keys.length === 0) return '{}';
	const parts = keys.map((k) => {
		const v = errs[k];
		if (v === true) return k;
		const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
		return `${!isNilOrWhitespace(k) ? k + ' ' : ''}${val}`;
	});
	return `${parts.join(', ')}`;
}
